import { Incoterms, PaymentEventEnum, PaymentMethodEnum } from 'api';
import {
  BankDetailsAutocomplete,
  BooleanSelectField,
  CheckboxField,
  PriceAndCurrencyField,
  SelectField,
  SwitchField,
  TextField,
  useFormContext,
  useWatch,
} from 'form';
import { type I18nTranslateFunction, useTranslation } from 'i18n';
import { useContext } from 'react';
import { Box, SectionContainer, SelectChangeEvent } from 'ui';

import { SalesOrderFormContext } from '../../contexts/SalesOrderFormContext';
import { SalesOrderCreateFormValues } from '../../pages/SalesOrderCreate/SalesOrderCreate';
import { VariablePricingFields } from '..';
import { InvoiceFinanceProviderSelectField } from '../InvoiceFinanceProvider/InvoiceFinanceProvider';
import { PaymentMethod } from '../PaymentMethod/PaymentMethod';

export const BuyerFinancialDetails = () => {
  const { t } = useTranslation('orders');
  const { setValue } = useFormContext<SalesOrderCreateFormValues>();
  const { variant, defaultValues } = useContext(SalesOrderFormContext);
  const { incoterm, purchaser_id, is_variable_pricing } = useWatch<SalesOrderCreateFormValues>();

  const handlePaymentMethodChange = (event: SelectChangeEvent) => {
    if (event.target.value === PaymentMethodEnum.DP) {
      setValue('due_date_offset', 0);
      setValue('due_date_direction', 1);
    }
  };

  return (
    <SectionContainer title={t('buyerFinancialDetails.title')}>
      <SwitchField name="is_variable_pricing" label={t('buyerFinancialDetails.labels.variablePricing')} />
      <PriceAndCurrencyField
        data-testid="price-per-tonne"
        slotProps={{
          currency: {
            inputProps: {
              'aria-label': t('buyerFinancialDetails.labels.sellerCurrency'),
            },
            name: 'buyer_currency',
          },
          price: {
            infoTooltip: t('buyerFinancialDetails.tooltips.pricePerTonne'),
            label: t('buyerFinancialDetails.labels.pricePerTonne'),
            name: 'price_per_tonne',
            required: true,
          },
        }}
      />
      <PriceAndCurrencyField
        data-testid="third-party-commission"
        slotProps={{
          currency: {
            inputProps: {
              'aria-label': t('buyerFinancialDetails.labels.thirdPartyCommissionCurrency'),
            },
            name: 'third_party_commission_currency',
          },
          price: {
            infoTooltip: t('buyerFinancialDetails.tooltips.thirdPartyCommissionPerTonne'),
            label: t('buyerFinancialDetails.labels.thirdPartyCommissionPerTonne'),
            name: 'third_party_commission_per_tonne',
            required: true,
          },
        }}
      />
      <PaymentMethod
        sx={{ gridColumn: 'span 2' }}
        required={true}
        infoTooltip={t('buyerFinancialDetails.tooltips.paymentMethod')}
        onChange={handlePaymentMethodChange}
      />
      <BooleanSelectField
        name="payment_must_be_received_before_safi_releases_the_documents"
        label={t('buyerFinancialDetails.labels.paymentBeforeDocRelease')}
        required
        infoTooltip={t('buyerFinancialDetails.tooltips.paymentBeforeDocRelease')}
      />
      <TextField
        type="number"
        label={t('buyerFinancialDetails.labels.depositPercentageUpfront')}
        name="deposit_percentage"
        infoTooltip={t('buyerFinancialDetails.tooltips.depositPercentageUpfront')}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <TextField
          type="number"
          label={t('buyerFinancialDetails.labels.dueDateOffset')}
          name="due_date_offset"
          sx={{ flexShrink: 1 }}
          required
        />
        <SelectField
          label=""
          data-testid="due_date_direction"
          name="due_date_direction"
          fullWidth={true}
          infoTooltip={t('buyerFinancialDetails.tooltips.dueDateDirection')}
          options={[
            {
              label: t('buyerFinancialDetails.dueDateDirection.before'),
              value: '-1',
            },
            {
              label: t('buyerFinancialDetails.dueDateDirection.after'),
              value: '1',
            },
          ]}
        />
      </Box>
      <SelectField
        label={t('buyerFinancialDetails.labels.paymentEvent')}
        name="payment_event"
        options={getPaymentEventOptions(incoterm, t)}
        infoTooltip={t('buyerFinancialDetails.tooltips.paymentEvent')}
      />
      <CheckboxField name="is_trial_order" label={t('buyerFinancialDetails.labels.isTrialOrder')} required />
      {variant === 'update' ? (
        <>
          <InvoiceFinanceProviderSelectField
            name="invoice_finance_provider"
            label={t('buyerFinancialDetails.labels.invoiceFinanceProvider')}
          />
          <BankDetailsAutocomplete
            name="bank_details_id"
            label={t('buyerFinancialDetails.labels.bankDetails')}
            tradeCompanies={purchaser_id ? [purchaser_id] : []}
            defaultValue={defaultValues?.bank_details_id}
          />
        </>
      ) : null}
      {is_variable_pricing ? <VariablePricingFields defaultValues={defaultValues} formVariant={variant} /> : null}
    </SectionContainer>
  );
};

const getPaymentEventOptions = (incoterm: Incoterms | undefined, t: I18nTranslateFunction<string>) => {
  switch (incoterm) {
    case 'EXW':
      return [
        {
          label: t('buyerFinancialDetails.paymentEvents.collectionFromSupplier'),
          value: PaymentEventEnum.CL,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.export'),
          value: PaymentEventEnum.XP,
        },
      ];
    case 'FOB':
      return [
        {
          label: t('buyerFinancialDetails.paymentEvents.collectionFromSupplier'),
          value: PaymentEventEnum.CL,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.export'),
          value: PaymentEventEnum.XP,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.billOfLading'),
          value: PaymentEventEnum.BL,
        },
      ];
    case 'CFR':
    case 'CIF':
      return [
        {
          label: t('buyerFinancialDetails.paymentEvents.collectionFromSupplier'),
          value: PaymentEventEnum.CL,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.export'),
          value: PaymentEventEnum.XP,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.billOfLading'),
          value: PaymentEventEnum.BL,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.destinationPortArrival'),
          value: PaymentEventEnum.DA,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.icdArrival'),
          value: PaymentEventEnum.IA,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.siteDelivery'),
          value: PaymentEventEnum.SD,
        },
      ];
    case 'CPT':
    case 'CIP':
      return [
        {
          label: t('buyerFinancialDetails.paymentEvents.billOfLading'),
          value: PaymentEventEnum.BL,
        },
      ];

    case 'DAP':
    case 'DDP':
      return [
        {
          label: t('buyerFinancialDetails.paymentEvents.collectionFromSupplier'),
          value: PaymentEventEnum.CL,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.export'),
          value: PaymentEventEnum.XP,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.billOfLading'),
          value: PaymentEventEnum.BL,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.destinationPortArrival'),
          value: PaymentEventEnum.DA,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.icdArrival'),
          value: PaymentEventEnum.IA,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.siteDelivery'),
          value: PaymentEventEnum.SD,
        },
      ];
    default:
      return [];
  }
};
